import { HiChevronRight } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import { track } from "@amplitude/analytics-browser";
import type { StoreSearchQuery } from "../../../__generated__/graphql";
import SuperKitchenLogo from "../assets/super-kitchen-logo.png";

type StoreSearchResultItem = NonNullable<
  StoreSearchQuery["storeSearch"]
>[number];

interface SearchResultsProps {
  loading: boolean;
  data: StoreSearchQuery | undefined;
  debouncedKeyword: string;
}

export default function SearchResults({
  loading,
  data,
  debouncedKeyword,
}: SearchResultsProps) {
  const navigate = useNavigate();
  const isSuperKitchenSearch = debouncedKeyword?.includes("슈퍼키친");

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data?.storeSearch || data.storeSearch.length === 0) {
    if (debouncedKeyword) {
      return (
        <div className="flex justify-center items-center h-full text-gray-500">
          검색 결과가 없습니다
        </div>
      );
    }
    return (
      <div className="flex flex-col justify-center items-center h-full text-gray-500">
        <p className="text-lg font-medium">오늘은 무슨 득템을 하게 될까요?</p>
        <p className="text-md">매일 기대되는 선착순 티켓팅</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isSuperKitchenSearch && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-5 border border-blue-100">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <img
                src={SuperKitchenLogo}
                alt="슈퍼키친 로고"
                className="w-16 h-16 rounded-full object-cover"
              />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                슈퍼키친이란?
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-2">
                슈퍼키친은 신선한 재료로 만든 건강한 식사와 반찬 요리를 제공하는
                프랜차이즈 매장입니다. 매일 새로운 메뉴와 합리적인 가격으로
                고객들에게 최고의 요리 경험을 선사합니다.
              </p>
              <p className="text-gray-600 text-xs">
                🍽️ 다양한 반찬과 요리 | 💰 합리적인 가격 | 🌱 신선한 재료
              </p>
            </div>
          </div>
        </div>
      )}

      {data.storeSearch.map((store: StoreSearchResultItem) => {
        if (!store) return null;
        return (
          <div
            key={store.id}
            onClick={() => {
              track("매장 클릭", {
                어디서: "검색 결과",
                storeId: store.id,
              });
              navigate(`/store/${store.id}`);
            }}
            className="flex items-center gap-4 p-4 border border-gray-200 rounded-2xl active:bg-gray-50 transition-colors"
          >
            {store.img && (
              <ResponsiveCDNImage
                src={store.img}
                alt={store.title}
                className="w-20 h-20 rounded-lg object-cover"
                sizeRatio={1 / 3}
              />
            )}
            <div className="flex-1">
              <div className="flex items-center gap-0.5">
                <span className="font-medium text-gray-900">{store.title}</span>
                <HiChevronRight className="w-4 h-4 text-gray-900" />
              </div>
              <p className="text-gray-500 mt-1">{store.address}</p>
              {store.distance != null && (
                <p className="text-gray-500 mt-1">
                  {store.distance.toFixed(1)}km
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
